import React, { useState } from "react";
import { NOW } from "@/lib/stages";
import { Btn, Fld, Inp } from "@/components/portal/crm/kit";
import { UkDate } from "./DeskDecision";
import { Modal } from "./Modal";

const TIMES = Array.from({ length: 25 }, (_, i) => `${String(8 + Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`);
const ukParts = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", year: "numeric", month: "2-digit",
    day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(d).map((x) => [x.type, x.value]));
  return [`${p.year}-${p.month}-${p.day}`, `${p.hour}:${p.minute}`];
};
/** The first half-hour slot still to come today (UK), or 10:00 tomorrow once the day's slots are gone. */
const nextSlot = () => {
  const [day, hm] = ukParts(new Date().toISOString());
  const mins = (t) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3));
  const slot = TIMES.find((t) => mins(t) > mins(hm) + 15);
  if (slot) return [day, slot];
  const t = new Date(`${day}T12:00:00Z`);
  t.setUTCDate(t.getUTCDate() + 1);
  return [t.toISOString().slice(0, 10), "10:00"];
};

/** Call back booked — the day and the UK time the desk will ring the client. */
function CallbackForm({ day, setDay, time, setTime, testId }) {
  return (
    <div className="grid2">
      <Fld label="Day"><UkDate value={day} onChange={setDay} testId={`${testId}-day`} /></Fld>
      <Fld label="Time (UK)">
        <select className="inp" value={time} onChange={(e) => setTime(e.target.value)} data-testid={`${testId}-time`}>
          {(TIMES.includes(time) ? TIMES : [time, ...TIMES]).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Fld>
    </div>
  );
}

/**
 * Post a Right now update. Waiting on and Next step come pre-filled (or with what was posted last time) and
 * can be edited; the introducer gets them by email. The stage itself stays Under assessment.
 * Mount it only while open — it reads its starting values once.
 */
export function NowDialog({ kind, current, onClose, onConfirm, testId = "now-dialog" }) {
  const def = NOW[kind] || {};
  const same = current?.key === kind ? current : null;
  const [start] = useState(() => (same?.callback_at && new Date(same.callback_at) > new Date() && ukParts(same.callback_at)) || nextSlot());
  const [waiting, setWaiting] = useState(same?.waiting || def.waiting || "");
  const [next, setNext] = useState(same?.next || def.next || "");
  const [day, setDay] = useState(start[0]);
  const [time, setTime] = useState(start[1]);
  const [busy, setBusy] = useState(false);

  const calling = kind === "callback";
  const submit = async () => {
    setBusy(true);
    try {
      await onConfirm({ waiting_on: waiting.trim() || null, next_step: next.trim() || null,
                        ...(calling ? { callback_at: `${day}T${time}` } : {}) });
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} width={560} testId={testId} title={`Right now: ${def.label || ""}`}
           sub="The stage stays Under assessment. These lines show on the case for the introducer and on the stage card."
           footer={
             <>
               <Btn onClick={onClose} testId={`${testId}-cancel`}>Not yet</Btn>
               <Btn kind="primary" onClick={submit} disabled={busy || (calling && !day)} testId={`${testId}-confirm`}>
                 {busy ? "Posting…" : calling ? "Book the call back" : "Post the update"}
               </Btn>
             </>
           }>
      {calling && <CallbackForm day={day} setDay={setDay} time={time} setTime={setTime} testId={`${testId}-callback`} />}
      <Fld label="Waiting on">
        <Inp value={waiting} onChange={(e) => setWaiting(e.target.value)} maxLength={200} data-testid={`${testId}-waiting`} />
      </Fld>
      <Fld label="Next step">
        <Inp value={next} onChange={(e) => setNext(e.target.value)} maxLength={200} data-testid={`${testId}-next`} />
      </Fld>
      <p style={{ color: "var(--mut)", fontSize: 12.5, margin: 0 }}>
        {def.pauses ? "The decision clock is paused while this is with the client or a third party. " : ""}
        The desk is never named to the introducer — it reads as Desk A, Desk B.
      </p>
    </Modal>
  );
}

export default NowDialog;
